import { Timestamp } from 'firebase/firestore';

// ============================================
// SPRINT MANAGEMENT (Jira-szerű)
// ============================================

export type SprintStatus = 'planned' | 'active' | 'completed';

export interface Sprint {
  id: string;
  organizationId: string; // Multi-tenant
  projectId: string;
  name: string;
  goal?: string;
  startDate: Date;
  endDate: Date;
  status: SprintStatus;
  taskIds: string[];
  
  // Metrikák
  velocity?: number; // Story points completed
  capacity?: number; // Team capacity (story points)
  completedStoryPoints?: number;
  totalStoryPoints?: number;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName?: string;
}

// ============================================
// CUSTOM FIELDS (Rugalmas adatkezelés)
// ============================================

export type CustomFieldType = 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'checkbox' | 'user' | 'url' | 'email' | 'phone';

export interface CustomFieldDefinition {
  id: string;
  organizationId: string; // Multi-tenant
  name: string;
  key: string; // Egyedi kulcs (pl. 'customer_priority')
  type: CustomFieldType;
  required: boolean;
  options?: string[]; // For select/multiselect
  defaultValue?: any;
  description?: string;
  
  // Hol használható
  applicableTo: ('project' | 'task' | 'quotation' | 'order' | 'delivery_note' | 'invoice')[];
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface ProjectCustomFields {
  [fieldKey: string]: any;
}

// ============================================
// PROJECT VIEW TYPES
// ============================================

export type ProjectViewType = 'board' | 'list' | 'gantt' | 'backlog' | 'calendar';

export interface ProjectDocument {
  id: string;
  name: string;
  originalName?: string;
  url: string;
  uploadedAt: Date | Timestamp | any; // Támogatja a Date, Timestamp és egyéb formátumokat
  type?: string;
  size?: number;
}

export interface Project {
  id: string;
  organizationId: string; // Multi-tenant - KÖTELEZŐ
  
  // Projekt azonosítók
  name: string;
  key: string; // Projekt kulcs (pl. "PROJ", "DEV", "SALES") - egyedi az organizationId-n belül
  description: string;
  color?: string; // Projekt szín (hex kód, pl. "#9333EA")
  icon?: string; // Projekt ikon (emoji vagy heroicon név)
  
  // Státusz és prioritás
  status: 'planning' | 'active' | 'completed' | 'paused' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  
  // Dátumok
  startDate: Date;
  endDate: Date;
  
  // Ügyfél kapcsolat
  customerId?: string;
  customerName?: string;
  
  // Csapat és jogosultságok
  assignedUsers: string[]; // User ID-k
  assignedUsersNames?: string[];
  teamLead?: string; // User ID
  teamLeadName?: string;
  
  // Projekt láthatóság és jogosultságok
  visibility: 'public' | 'private' | 'team'; // public: mindenki látja, private: csak assignedUsers, team: organizationId alapján
  allowedRoles?: ('admin' | 'user' | 'customer')[]; // Mely szerepkörök férhetnek hozzá
  
  // Feladatok és sprintek
  taskIds: string[];
  sprintIds?: string[]; // Sprint ID-k
  activeSprintId?: string; // Aktív sprint ID
  backlogTaskIds?: string[]; // Backlog feladatok (nincs sprinthez rendelve)
  
  // Haladás és metrikák
  progress: number; // 0-100%
  completedTasks?: number;
  totalTasks?: number;
  velocity?: number; // Átlagos velocity (story points/sprint)
  
  // Pénzügy
  budget?: number;
  actualCost?: number;
  currency?: string;
  
  // Egyedi mezők (Jira-szerű)
  customFields?: ProjectCustomFields;
  
  // Dokumentumok és kapcsolatok
  tags: string[];
  notes?: string;
  documents: ProjectDocument[];
  
  // Dokumentum kapcsolatok (Pipeline integráció)
  quotationIds?: string[];
  orderIds?: string[];
  deliveryNoteIds?: string[];
  invoiceIds?: string[];
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy: string; // User ID
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
  
  // Archív és törlés
  isActive: boolean;
  isArchived?: boolean;
  archivedAt?: Date;
  archivedBy?: string;
}

export interface CreateProjectData {
  organizationId: string; // KÖTELEZŐ - Multi-tenant
  
  // Projekt azonosítók
  name: string;
  key: string; // Projekt kulcs (pl. "PROJ")
  description: string;
  color?: string; // Projekt szín (hex kód)
  icon?: string; // Projekt ikon (emoji)
  
  // Státusz és prioritás
  status: Project['status'];
  priority: Project['priority'];
  
  // Dátumok
  startDate: Date;
  endDate: Date;
  
  // Ügyfél
  customerId?: string;
  customerName?: string;
  
  // Csapat
  assignedUsers: string[];
  assignedUsersNames?: string[];
  teamLead?: string;
  teamLeadName?: string;
  
  // Jogosultságok
  visibility: Project['visibility'];
  allowedRoles?: Project['allowedRoles'];
  
  // Haladás
  progress: number;
  
  // Pénzügy
  budget?: number;
  currency?: string;
  
  // Egyedi mezők
  customFields?: ProjectCustomFields;
  
  // Dokumentumok
  tags: string[];
  notes?: string;
  documents: ProjectDocument[];
}

export interface ProjectFormData extends CreateProjectData {
  // Form-specific fields if needed
}

export interface ProjectFilters {
  organizationId?: string; // Multi-tenant szűrés
  status?: Project['status'];
  priority?: Project['priority'];
  assignedUser?: string;
  customerId?: string;
  teamLead?: string;
  visibility?: Project['visibility'];
  isArchived?: boolean;
  search?: string;
  tags?: string[];
  dateFrom?: Date;
  dateTo?: Date;
}

export interface ProjectStats {
  total: number;
  planning: number;
  active: number;
  completed: number;
  paused: number;
  cancelled: number;
  byPriority: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  averageProgress: number;
  overdueCount: number;
}

// Státusz opciók
export const PROJECT_STATUS_OPTIONS = [
  { value: 'planning', label: 'Tervezés', color: 'gray' },
  { value: 'active', label: 'Aktív', color: 'blue' },
  { value: 'completed', label: 'Befejezett', color: 'green' },
  { value: 'paused', label: 'Szünetel', color: 'yellow' },
  { value: 'cancelled', label: 'Megszakítva', color: 'red' }
] as const;

// Prioritás opciók
export const PROJECT_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Alacsony', color: 'green' },
  { value: 'medium', label: 'Közepes', color: 'yellow' },
  { value: 'high', label: 'Magas', color: 'orange' },
  { value: 'urgent', label: 'Sürgős', color: 'red' }
] as const; 